import React, { useState } from 'react';

import {
    Button, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Menu, MenuItem,
} from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { DataGrid, GridColumnHeaderSortIcon } from '@mui/x-data-grid';
import _ from 'lodash';

import { useSelector } from 'BMapsRedux';
import { UserActions } from 'BMapsCmds';
import BMapsTooltip from 'BMapsUI/BMapsTooltip';
import DialogContentSection from 'BMapsUI/common/DialogContentSection';
import { App } from 'BMapsSrc/BMapsApp';
import { ensureArray } from 'BMapsSrc/util/js_utils';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { LoadingScreen } from 'BMapsSrc/LoadingScreen';
import { loadProtein } from 'BMapsSrc/project_data';
import {
    setTargetsTableState, addGroupToProjectTargets,
    asIndividualTarget, getEntryMapCases, isGroupEntry,
} from 'BMapsRedux/projectState/access';
import { buildCuratedKinasePanels, structureKey } from 'BMapsModel';
import { userConfirmation } from 'BMapsSrc/utils';
import { CustomColumnsPanel } from 'BMapsUI/tables/CustomColumnsPanel';
import { GridToolBar } from 'BMapsUI/tables/CustomToolbar';
import { Loader } from 'BMapsSrc/Loader';
import { DialogCancelButton, DialogPrimaryButton } from 'BMapsUI/common/DialogActionButtons';
import { releaseUnusedConnections } from 'BMapsSrc/server/connection_utils';
import { wrapForDisabledTooltip } from 'BMapsUI/common/helpers';

function getDefaultColumnVisibilityModel(targetRows) {
    const columnVisibilityModel = {};
    for (const row of targetRows) {
        for (const [key, value] of Object.entries(row)) {
            if (key === 'id') continue;
            if (key === 'actions') continue;
            columnVisibilityModel[key] = false;
        }
    }
    return {
        ...columnVisibilityModel,
        isTarget: true,
        actions: true,
        isLoaded: true,
        displayName: true,
        pdbID: true,
    };
}

export default function ProjectTargetsTab({ selected }) {
    const { targetList } = useSelector((state) => state.projectState);
    const tableState = useSelector((state) => state.projectState.tableState.targetsTable);
    const loadedStructures = App.Workspace.getLoadedProteins();
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [membersGroupId, setMembersGroupId] = useState(null);
    const [panelMenuAnchor, setPanelMenuAnchor] = useState(null);
    const [curatedPanels, setCuratedPanels] = useState([]);

    // Row id: a group is keyed by its groupId, an individual target by its (mount-bearing)
    // mapCase uri — keeping the individual key identical to the pre-groups behavior.
    const rowIdOf = (entry) => (isGroupEntry(entry) ? entry.groupId : entry.mapCase.uri);
    const findEntryByRowId = (id) => targetList.find((t) => rowIdOf(t) === id);

    // The curated panel whose member picker (sub-table) is open, resolved live from the
    // target list so it reflects members already pulled out as targets/off-targets.
    const membersGroup = membersGroupId != null
        ? targetList.find((t) => t.groupId === membersGroupId)
        : null;

    function setTarget(entryId, isTarget) {
        const entry = findEntryByRowId(entryId);
        if (!entry || isGroupEntry(entry)) return; // groups carry no target/off-target flag
        UserActions.SetProteinTarget(entry.mapCase, { ...entry, isTarget });
    }

    // Curated kinase GroupTargets (DFG-out / DFG-in / representative) available from the
    // structures across data connections. The button opens a menu so the user can add a single
    // panel (or all) rather than always adding every panel. Query-only: members are added as
    // unloaded targets (FDQ runs against unloaded structures of interest).
    function openCuratedPanelMenu(event) {
        const allCases = App.Workspace.collectFromCaseDataCollections((cd) => cd.getMapList());
        setCuratedPanels(buildCuratedKinasePanels(allCases));
        setPanelMenuAnchor(event.currentTarget);
    }
    function closeCuratedPanelMenu() {
        setPanelMenuAnchor(null);
    }
    function addCuratedPanel(panel) {
        addGroupToProjectTargets(panel.groupId, panel.displayName, panel.mapCases, { source: 'curated' });
        closeCuratedPanelMenu();
    }
    function addAllCuratedPanels() {
        curatedPanels.forEach((panel) => addGroupToProjectTargets(
            panel.groupId, panel.displayName, panel.mapCases, { source: 'curated' },
        ));
        closeCuratedPanelMenu();
    }
    // GroupIds already in the target list, so the menu can disable panels that are present.
    const addedGroupIds = new Set(targetList.filter(isGroupEntry).map((entry) => entry.groupId));
    const allCuratedPanelsAdded = curatedPanels.length > 0
        && curatedPanels.every((panel) => addedGroupIds.has(panel.groupId));

    async function loadTarget(entryIds) {
        const idList = ensureArray(entryIds);
        const mapCasePairs = [];
        for (const id of idList) {
            const entry = findEntryByRowId(id);
            // Groups are query-only: their members are not loaded into the workspace here.
            if (!entry || isGroupEntry(entry)) continue;
            mapCasePairs.push({ mapCase: entry.mapCase, isTarget: entry.isTarget });
        }
        const unique = _.uniqBy(mapCasePairs, (p) => p.mapCase.uri);
        if (unique.length === 0) return;

        const mapcasesWithLoadOptions = unique.map(({ mapCase, isTarget }) => ({
            mapCase,
            loadOptions: { keepExisting: true },
            dataConnection: App.PrimaryDataConnection,
            isTarget,
        }));
        await releaseUnusedConnections();
        const loadingNoun = unique.length === 1
            ? unique[0].mapCase.displayName
            : `${unique.length} structures`;
        const loadMsg = `Loading ${loadingNoun} into workspace...`;
        const loadingPromise = LoadingScreen.withLoadingScreen((async () => {
            const errMap = new Map();
            for (const { mapCase, loadOptions, dataConnection } of mapcasesWithLoadOptions) {
                const { error } = await loadProtein({
                    mapCase,
                    loadOptions,
                    dataConnection,
                    loadMsg: `Loading ${mapCase.displayName}...`,
                });
                if (error) errMap.set(mapCase, error);
            }
        })(), loadMsg); // IIFE since withLoadingScreen needs a promise, not a function
        await EventBroker.withIgnoredEvents('escapeKey', loadingPromise);
        // Refresh the grid so the newly-loaded structures flip to "Loaded". Membership is
        // unchanged; re-dispatching the same list triggers a re-render that recomputes the
        // isLoaded column from the Workspace model.
        UserActions.SetAllTargetInfo([...targetList]);
    }

    /**
     *
     * @param {string[] | string} uri
     * @param {boolean} removeFromProject
     * @returns
     */
    async function removeFromWorkspace(entryIds, removeFromProject) {
        const idList = ensureArray(entryIds);
        const idsToRemove = new Set(idList);

        const mapCaseList = [];
        for (const id of idList) {
            const entry = findEntryByRowId(id);
            // Groups are query-only (no workspace state): they carry no mapCase to unload, and
            // are dropped via the targetList filter below when removing from the project.
            if (!entry || isGroupEntry(entry)) continue;
            mapCaseList.push(entry.mapCase);
        }
        const uniqueMapCases = _.uniqBy(mapCaseList, (mc) => mc.uri);

        const userCompounds = [];
        for (const mapCase of uniqueMapCases) {
            const cdata = App.Workspace.lookupCaseData(mapCase);
            const userCmpds = cdata ? cdata.getCompounds().filter((c) => !c.isLigand()) : [];
            userCompounds.push(...userCmpds);
        }
        const totalCount = uniqueMapCases.length;
        const needPermissionToRemove = removeFromProject || userCompounds.length > 0;
        if (needPermissionToRemove) {
            const noun = totalCount === 1 ? 'structure' : 'structures';
            const nounPhrase = `${totalCount === 1 ? 'this' : 'these'} ${noun}`;
            const compoundNoun = userCompounds.length === 1 ? 'compound' : 'compounds';
            const cmpdWarning = userCompounds.length > 0 ? `\n\nThis will permanently remove ${userCompounds.length} user ${compoundNoun} associated with ${nounPhrase}. If you wish to preserve any compounds, export them before proceeding.` : '';
            const verb = removeFromProject ? 'remove' : 'unload';
            const target = removeFromProject ? 'project' : 'workspace';
            const title = `${_.capitalize(verb)} ${_.capitalize(noun)} from ${_.capitalize(target)}?`;
            const clear = await userConfirmation(title, `Are you sure you want to ${verb} ${nounPhrase} from the ${target}?${cmpdWarning}`);
            if (!clear) {
                return;
            }
        }
        await releaseUnusedConnections();
        for (const mapCase of uniqueMapCases) {
            const caseData = App.Workspace.lookupCaseData(mapCase);
            if (caseData) {
                await EventBroker.withIgnoredEvents('escapeKey', (async () => {
                    // Plain "Unload" keeps the target in the project list (so its row flips to
                    // "Not loaded" rather than disappearing); "Remove from project" lets it evict.
                    await UserActions.RemoveCase(caseData, {
                        removeFromProjectTargets: removeFromProject,
                    });
                })());
            }
        }
        if (removeFromProject) {
            // rowIdOf covers both shapes, so this also drops any selected curated groups.
            const newTargetList = targetList.filter((t) => !idsToRemove.has(rowIdOf(t)));
            UserActions.SetAllTargetInfo(newTargetList);
        } else {
            // Unload only: RemoveCase kept the target in the list (removeFromProjectTargets=false),
            // so re-dispatch the same list to trigger a re-render that flips the isLoaded column
            // to "Not loaded".
            UserActions.SetAllTargetInfo([...targetList]);
        }
    }

    const {
        columns,
        targetRows,
    } = buildTargetsGrid(
        targetList, loadedStructures, loadTarget, removeFromWorkspace, setTarget, setMembersGroupId
    );

    return (
        <>
            {!!selected && (
                <DialogContentSection
                    title="Project Targets"
                >
                    <Box sx={{ paddingBottom: '0.5rem' }}>
                        <BMapsTooltip
                            title="Choose a curated kinase selectivity panel to add as project targets (DFG-out, DFG-in, or one representative structure per kinase) — or add all"
                        >
                            <Button
                                variant="outlined"
                                startIcon={<AddCircleOutlineIcon />}
                                onClick={openCuratedPanelMenu}
                            >
                                Add curated kinase panels
                            </Button>
                        </BMapsTooltip>
                        <Menu
                            anchorEl={panelMenuAnchor}
                            open={!!panelMenuAnchor}
                            onClose={closeCuratedPanelMenu}
                        >
                            {curatedPanels.length === 0
                                ? <MenuItem disabled>No curated kinase library found</MenuItem>
                                : [
                                    ...curatedPanels.map((panel) => {
                                        const alreadyAdded = addedGroupIds.has(panel.groupId);
                                        return (
                                            <MenuItem
                                                key={panel.groupId}
                                                disabled={alreadyAdded}
                                                onClick={() => addCuratedPanel(panel)}
                                            >
                                                {`${panel.displayName} (${panel.mapCases.length})`}
                                                {alreadyAdded ? ' — added' : ''}
                                            </MenuItem>
                                        );
                                    }),
                                    <MenuItem
                                        key="__add_all_curated_panels__"
                                        disabled={allCuratedPanelsAdded}
                                        onClick={addAllCuratedPanels}
                                    >
                                        Add all panels
                                    </MenuItem>,
                                ]}
                        </Menu>
                    </Box>
                    <DataGrid
                        rows={targetRows}
                        columns={columns}
                        disableRowSelectionOnClick
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 10, page: 0 },
                            },
                        }}
                        pageSizeOptions={[5, 10, 20]}
                        onRowSelectionModelChange={(model) => {
                            setSelectedRowIds([...model.ids]);
                        }}
                        onColumnVisibilityModelChange={(columnVisibilityModel) => {
                            setTargetsTableState({ columnVisibilityModel });
                        }}
                        columnVisibilityModel={tableState?.columnVisibilityModel
                            || getDefaultColumnVisibilityModel(targetRows)}
                        slots={{
                            toolbar: (toolBarProps) => (
                                <GridToolBar
                                    {...toolBarProps}
                                    useFilter
                                    exportFileName="Project-Targets"
                                    toolBarId="targetStructuresTable"
                                />
                            ),
                            columnsPanel: CustomColumnsPanel,
                            columnHeaderSortIcon: (props) => {
                                const found = columns.find((c) => c.field === props.field);
                                const title = found ? `Sort by ${found.headerName}` : 'Sort';
                                return (
                                    <BMapsTooltip title={title} noMaxWidth>
                                        <GridColumnHeaderSortIcon {...props} />
                                    </BMapsTooltip>
                                );
                            },
                        }}
                        showToolbar
                        checkboxSelection
                        slotProps={{
                            basePopper: { placement: 'bottom-start' },
                        }}
                    />

                    <TableSelectionButtons
                        selectedRowIds={selectedRowIds}
                        loadTarget={loadTarget}
                        removeFromWorkspace={removeFromWorkspace}
                        targetRows={targetRows}
                    />
                </DialogContentSection>
            )}
            <MembersDialog
                group={membersGroup}
                targetList={targetList}
                onClose={() => setMembersGroupId(null)}
            />
        </>
    );
}

function TableSelectionButtons({
    selectedRowIds, loadTarget, removeFromWorkspace, targetRows,
}) {
    const selectedItems = targetRows.filter((r) => selectedRowIds.includes(r.id));
    // Groups are query-only: they can be removed but not loaded/unloaded.
    const loadableItems = selectedItems.filter((r) => !r.isGroup);
    const [loadedStructures, unloadedStructures] = _.partition(loadableItems, (r) => r.isLoaded);

    const disableLoadButton = unloadedStructures.length < 1;
    const disableUnloadButton = loadedStructures.length < 1;
    const disableRemoveButton = selectedItems.length < 1;
    const disableClearProjectButton = targetRows.length < 1;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flexWrap: 'wrap',
            gap: '1rem',
            paddingTop: '1rem',
            width: window.screen.width > 1000 ? '24rem' : '100%',
        }}
        >
            <BMapsTooltip
                title={disableLoadButton
                    ? 'Select an unloaded structure to load into the workspace'
                    : 'Load selected structures into the workspace'}
            >
                {wrapForDisabledTooltip(
                    <DialogPrimaryButton
                        onClick={() => {
                            const uriList = unloadedStructures
                                .map((item) => item.id);
                            loadTarget(uriList);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableLoadButton}
                    >
                        Load Selected
                    </DialogPrimaryButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableUnloadButton
                    ? 'Select a loaded structure to unload from the workspace'
                    : 'Unload selected structures from the workspace'}
            >
                {wrapForDisabledTooltip(
                    <DialogCancelButton
                        onClick={() => {
                            const uriList = loadedStructures
                                .map((item) => item.id);
                            removeFromWorkspace(uriList);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableUnloadButton}
                    >
                        Unload Selected
                    </DialogCancelButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableRemoveButton
                    ? 'Select a structure to remove from the workspace and from the project targets'
                    : 'Remove selected structures from the workspace and from the project targets'}
            >
                {wrapForDisabledTooltip(
                    <DialogCancelButton
                        onClick={() => {
                            const uriList = selectedItems.map((item) => item.id);
                            removeFromWorkspace(uriList, true);
                        }}
                        sx={{ width: '100%' }}
                        disabled={disableRemoveButton}
                    >
                        Remove Selected From The Project
                    </DialogCancelButton>
                )}
            </BMapsTooltip>
            <BMapsTooltip
                title={disableClearProjectButton
                    ? 'No structures or project info to remove'
                    : 'Remove all structures from the workspace and from the project targets'}
            >
                {wrapForDisabledTooltip(
                    <Button
                        variant="contained"
                        color="error"
                        onClick={
                            () => {
                                const uriList = targetRows.map((item) => item.id);
                                removeFromWorkspace(uriList, true);
                            }
                        }
                        disabled={disableClearProjectButton}
                        sx={{ width: '100%' }}
                    >
                        Clear Project
                    </Button>
                )}
            </BMapsTooltip>
        </Box>
    );
}

function buildTargetsGrid(
    targetList, loadedStructures, loadTarget, removeFromWorkspace, setTarget, openMembers
) {
    const columnNames = [
        'displayName', 'pdbID', 'isTarget', 'isLoaded',
        'organism', 'description',
        'gene_name', 'sourceDesc', 'lastUpdate',
        'dataSource',
    ];
    if (Loader.AllowLabFeatures) {
        columnNames.push('uri', 'project', 'case', 'projectCase');
    }

    // add scopedProperties and otherProperties fields from individual entries only
    for (const target of targetList) {
        if (isGroupEntry(target)) continue;
        const { mapCase } = target;
        const properties = mapCase.listProperties({ includeChildren: true });
        for (const property of properties) {
            if (!columnNames.includes(property.name)) {
                columnNames.push(property.name);
            }
        }
        if (!mapCase.otherProperties) continue;
        for (const key of Object.keys(mapCase.otherProperties)) {
            if (!columnNames.includes(key)) {
                columnNames.push(key);
            }
        }
    }
    columnNames.push('actions');

    const columns = columnNames.map((name) => {
        switch (name) {
            case 'displayName':
                return {
                    field: name,
                    headerName: 'Name',
                    title: 'Name',
                    flex: 2,
                };
            case 'isTarget':
                return {
                    field: name,
                    headerName: 'Is Target',
                    title: 'Is Target',
                    flex: 1,
                    renderCell: ({ row }) => (
                        // Groups have no target/off-target flag (members are scored only when
                        // pulled out as individuals), so there is no checkbox for group rows.
                        row.isGroup ? null : (
                            <Checkbox
                                checked={row.isTarget === true}
                                onClick={() => {
                                    setTarget(row.id, !row.isTarget);
                                }}
                            />
                        )
                    ),
                };
            case 'isLoaded':
                return {
                    field: name,
                    headerName: 'Workspace',
                    title: 'Workspace',
                    flex: 1,
                    renderCell: ({ row }) => {
                        if (row.isGroup) return '—'; // groups are query-only, not loaded here
                        return row.isLoaded ? 'Loaded' : 'Not loaded';
                    },
                };
            case 'actions':
                return {
                    field: name,
                    headerName: 'Actions',
                    title: 'Actions',
                    flex: 1,
                    minWidth: 75,
                    sortable: false,
                    filterable: false,
                    align: 'right',
                    headerAlign: 'right',
                    renderCell: ({ row }) => {
                        const actions = [];
                        // Groups are query-only: no load/unload, only remove-from-project.
                        if (!row.isGroup && row.isLoaded) {
                            actions.push(
                                <BMapsTooltip title="Unload this structure from the workspace">
                                    <IconButton
                                        size="small"
                                        onClick={() => removeFromWorkspace(row.id)}
                                    >
                                        <RemoveCircleOutlineIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        } else if (!row.isGroup) {
                            actions.push(
                                <BMapsTooltip title="Load this structure into the workspace">
                                    <IconButton
                                        size="small"
                                        onClick={() => loadTarget(row.id)}
                                    >
                                        <AddCircleOutlineIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        }
                        if (row.isGroup) {
                            actions.push(
                                <BMapsTooltip title="Pick target / off-target structures from this panel">
                                    <IconButton size="small" onClick={() => openMembers(row.id)}>
                                        <FormatListBulletedIcon fontSize="inherit" />
                                    </IconButton>
                                </BMapsTooltip>
                            );
                        }
                        actions.push(
                            <BMapsTooltip title="Remove this structure from the workspace and the project">
                                <IconButton
                                    size="small"
                                    onClick={() => removeFromWorkspace(row.id, true)}
                                >
                                    <ClearIcon fontSize="inherit" />
                                </IconButton>
                            </BMapsTooltip>,
                        );
                        return (
                            <Box>
                                {actions.map((a) => (
                                    // Actions are BMapsTooltips with unique titles
                                    <React.Fragment key={a.props.title}>{a}</React.Fragment>
                                ))}
                            </Box>
                        );
                    },
                };
            case 'lastUpdate': {
                return {
                    field: name,
                    headerName: 'Last Update',
                    title: 'Last Update',
                    type: 'dateTime',
                    minWidth: 120,
                    renderCell: ({ value, row }) => {
                        if (!value) return null;
                        const displayValue = new Date(value).toLocaleString('en-US', {
                            dateStyle: 'medium',
                            timeStyle: 'short',
                        });
                        const tooltip = displayValue;
                        return (
                            <BMapsTooltip title={tooltip}>
                                <span style={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                >
                                    {displayValue}
                                </span>
                            </BMapsTooltip>
                        );
                    },
                };
            }
            case 'sourceDesc': {
                return {
                    field: name,
                    headerName: 'Source',
                    title: 'Source',
                    flex: 1,
                };
            }
            case 'pdbID': {
                return {
                    field: name,
                    headerName: 'PDB ID',
                    title: 'PDB ID',
                    flex: 1,
                };
            }
            case 'gene_name': {
                return {
                    field: name,
                    headerName: 'Gene Name',
                    title: 'Gene Name',
                    flex: 1,
                };
            }
            case 'dataSource': {
                return {
                    field: name,
                    headerName: 'Data Source',
                    title: 'Data Source',
                    flex: 1,
                };
            }
            case 'molecule_name': {
                return {
                    field: name,
                    headerName: 'Molecule Name',
                    title: 'Molecule Name',
                    flex: 1,
                };
            }
            case 'projectCase': {
                return {
                    field: name,
                    headerName: 'Project Case',
                    title: 'Project Case',
                    flex: 1,
                };
            }
            case 'uri': {
                return {
                    field: name,
                    headerName: 'URI',
                    title: 'URI',
                    flex: 1,
                };
            }

            default:
                return {
                    field: name, headerName: name, title: name, flex: 1,
                };
        }
    });
    const individualEntries = targetList.filter(asIndividualTarget);
    const groupEntries = targetList.filter(isGroupEntry);

    const individualRows = _.uniqBy(
        individualEntries,
        (t) => t.mapCase.uri
    ).map((target) => {
        const row = {
            ...target.mapCase.otherProperties,
            ...Object.fromEntries(
                target.mapCase
                    .listProperties({ includeChildren: true })
                    .map((p) => [p.name, p.value]),
            ),
            id: target.mapCase.uri,
            displayName: target.mapCase.displayName,
            isTarget: target.isTarget,
            isLoaded: loadedStructures.includes(target.mapCase),
            pdbID: target.mapCase.pdbID,
            description: target.mapCase.description,
            molecule_name: target.mapCase.molecule_name,
            gene_name: target.mapCase.gene_name,
            lastUpdate: target.mapCase.lastUpdate,
            organism: target.mapCase.organism,
            sourceDesc: target.mapCase.sourceDesc,
            data_format: target.mapCase.data_format,
            dataSource: target.mapCase.dataSource?.name,
        };
        if (Loader.AllowDevFeatures) {
            row.projectCase = target.mapCase.projectCase;
            row.case = target.mapCase.case;
            row.project = target.mapCase.project;
            row.uri = target.mapCase.uri;
        }
        return row;
    }).filter((t) => t.id);

    // Curated groups render as single rows (query-only: name + member count + target/off-target);
    // load/unload don't apply to a group, only "remove from project".
    const groupRows = groupEntries.map((group) => ({
        id: group.groupId,
        isGroup: true,
        displayName: `${group.displayName} (${getEntryMapCases(group).length})`,
        isLoaded: false,
        sourceDesc: group.source ?? 'curated',
    }));

    const targetRows = [...groupRows, ...individualRows];
    if (targetRows.length === 0) {
        return { targetRows: [], columns: [] };
    }
    return {
        targetRows, columns,
    };
}

function memberLabel(mapCase) {
    const kinaseId = mapCase.otherProperties?.kinaseId;
    // Use `case` (the prep directory name, e.g. 4WSQ_A / 6XR6_alt3) rather than the
    // bare pdbID, so chain/alt variants of the same PDB are distinguishable.
    const variant = mapCase.case || mapCase.pdbID || '';
    if (kinaseId && variant) return `${kinaseId} — ${variant}`;
    return kinaseId || mapCase.displayName || variant;
}

function memberMatches(mapCase, query) {
    const fields = [
        mapCase.otherProperties?.kinaseId, mapCase.pdbID, mapCase.case,
        mapCase.displayName, mapCase.molecule_name,
    ];
    return fields.some((f) => f && f.toLowerCase().includes(query));
}

// A searchable picker over a curated panel's members: pull a structure into the project
// targets as a target or off-target (a structure of interest). The panel itself stays a
// reference group; the pulled-out individual overrides it for scoring + the coral map.
function MembersDialog({ group, targetList, onClose }) {
    const [search, setSearch] = useState('');
    if (!group) return null;

    const members = getEntryMapCases(group);
    const query = search.trim().toLowerCase();
    const filtered = query ? members.filter((mc) => memberMatches(mc, query)) : members;

    // Current per-structure role (keyed mount-free) so already-pulled members show it.
    const roleByKey = new Map(
        targetList
            .filter((t) => !isGroupEntry(t))
            .map((t) => [structureKey(t.mapCase), t.isTarget ? 'Target' : 'Off-target'])
    );

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{`${group.displayName} — choose targets / off-targets`}</DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    placeholder="Search by kinase, PDB, or name"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ mb: 1 }}
                />
                <Box sx={{ maxHeight: '24rem', overflow: 'auto' }}>
                    {filtered.map((mapCase) => {
                        const currentRole = roleByKey.get(structureKey(mapCase));
                        return (
                            <Box
                                key={structureKey(mapCase)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                                }}
                            >
                                <Box sx={{ flex: 1 }}>{memberLabel(mapCase)}</Box>
                                {currentRole && (
                                    <Box sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                                        {currentRole}
                                    </Box>
                                )}
                                <Button
                                    size="small"
                                    aria-label={`Add ${memberLabel(mapCase)} as target`}
                                    onClick={() => UserActions.AddStructureOfInterest(
                                        mapCase, { isTarget: true },
                                    )}
                                >
                                    Target
                                </Button>
                                <Button
                                    size="small"
                                    aria-label={`Add ${memberLabel(mapCase)} as off-target`}
                                    onClick={() => UserActions.AddStructureOfInterest(
                                        mapCase, { isTarget: false },
                                    )}
                                >
                                    Off-target
                                </Button>
                            </Box>
                        );
                    })}
                    {filtered.length === 0 && (
                        <Box sx={{ color: 'text.secondary', py: 1 }}>No matching structures.</Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Done</Button>
            </DialogActions>
        </Dialog>
    );
}
